import { computed, useAsync } from '@nuxtjs/composition-api';
import { useQuery } from 'villus';
import { useCachedSsrRef } from './serverCache';
import { useSetLocaleToCacheParams } from './i18n';
import { isValidOffer, mapHomeOffer, OffersContent } from './home';
import { HomeOffersDocument, HomeOffersQuery } from '~/graphql/Offers';
import { CategoryPageOfferDocument, CategoryPageOfferQueryVariables } from '~/graphql/Category';
import { MaybeReactive, Unpacked } from '~/types/utils';
import { OfferCardFragment } from '~/graphql/fragments';

export type OfferType = Array<Unpacked<OffersContent> & OfferCardFragment>;

export function useHomepageOffers(limit = 6) {
  const { cacheParam } = useSetLocaleToCacheParams();
  const offers = useCachedSsrRef<HomeOffersQuery['homepage_offers']>(cacheParam('offers'), []);

  const { execute, isFetching } = useQuery({
    query: HomeOffersDocument,
    variables: {
      limit,
    },
    fetchOnMount: false,
  });

  useAsync(async () => {
    // Don't refetch offers unless cache expired
    if (offers.value.length) {
      return;
    }

    const { data, error } = await execute();

    if (error) {
      throw new Error(error.message);
    }

    offers.value = (data?.homepage_offers || []).sort((a, b) => Number(a?.sort) - Number(b?.sort));
  });

  return {
    offers,
    isFetching,
  };
}

export function useCategoryOffers(variables?: MaybeReactive<CategoryPageOfferQueryVariables>) {
  const { data, isFetching } = useQuery({
    query: CategoryPageOfferDocument,
    variables,
  });
  return {
    isFetching,
    offers: computed(() => data?.value?.category?.offers?.filter(isValidOffer)?.map(mapHomeOffer)),
    listingAds: computed(() => data?.value?.category?.listingAds?.filter(isValidOffer)?.map(mapHomeOffer)),
  };
}
